<template>
  <div>
    <app-loading :loading="!integrationsState.state.loaded" />
    <template v-if="integrationsState.state.loaded">
      <div v-if="entity && !isPgpEnabled" class="panel-tag">Columns needed {{ displayColumns }}</div>

      <file-pond
        ref="file"
        :label-idle="emptyFileLabel"
        @addfile="onAddFile"
        @removefile="onRemoveFile"
        :accepted-file-types="acceptedFileTypes"
        :allow-multiple="canUploadMultiFile"
        :max-files="canUploadMultiFile ? 50 : 1"
        file-validate-type-label-expected-types="Expects a CSV file"
      />

      <template v-if="!canUploadMultiFile">
        <sme-alert level="danger" v-if="singleUpload.validationErrors.invalidCharacters">
          <b>The file has invalid characters, please fix them before uploading the file</b>
          <br />
          Please fix the file or contact support
        </sme-alert>

        <slot name="extraErrors"></slot>
        <sme-alert level="danger" v-if="singleUpload.validationErrors.missing">
          <b>Errors found in your csv column names:</b>
          <ul>
            <li>Missing columns: {{ singleUpload.validationErrors.missing }}</li>
            <li v-if="singleUpload.validationErrors.additional">
              Additional columns (not expected): {{ singleUpload.validationErrors.additional }}
            </li>
          </ul>
          Please fix the column headers or contact support
        </sme-alert>

        <sme-alert level="danger" v-if="singleUpload.validationErrors.validationReport">
          <b>Errors found in your csv data:</b>
          <ul>
            <li v-for="item in singleUpload.validationErrors.validationReport" :key="item">
              {{ item }}
            </li>
          </ul>
          Please fix data errors or contact support
        </sme-alert>

        <sme-alert level="danger" v-if="hasBadFileName">
          <b>Invalid file name</b>
          <br />
          <p>Only allowed file name values below:</p>
          <ul>
            <li v-for="item in singleUpload.validationErrors.allowedFileNames" :key="item">
              {{ item }}
            </li>
          </ul>
          Please fix data errors or contact support
        </sme-alert>

        <sme-alert level="good" v-if="successfullyValidated">
          <p>Successful validation</p>
        </sme-alert>

        <sme-alert
          level="warning"
          icon="exclamation-triangle"
          v-if="!singleUpload.validationErrors.missing && singleUpload.validationErrors.additional"
        >
          <b>Additional columns (not expected): </b>
          <ul>
            <li>{{ singleUpload.validationErrors.additional }}</li>
          </ul>
        </sme-alert>
      </template>

      <sme-alert level="info" v-if="validationSkipped" class="mt-3">
        <b>This file is PGP-encrypted, so it can't be previewed here.</b>
        <p class="mb-0">
          {{ skipReason }} You can upload it as normal — it will be validated automatically after upload.
        </p>
      </sme-alert>

      <div v-if="error" class="col-md-12 mt-3">
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div class="alert alert-danger" v-html="error"></div>
      </div>

      <div v-if="hasFiles && showValidationForEntity && !isPgpEnabled" class="mt-3 mb-3">
        <div class="d-flex align-items-center">
          <b-button @click="validateCsv" :disabled="validating || uploading" variant="outline-primary" class="mr-3">
            <b-spinner v-if="validating" small class="mr-2" />
            <font-awesome-icon v-else :icon="['fal', 'shield-check']" class="mr-2" />
            {{ validateButtonText }}
          </b-button>
          <small class="text-muted">
            Check your {{ canUploadMultiFile ? 'files' : 'file' }} for data errors before uploading
          </small>
        </div>
      </div>

      <template v-if="canUploadMultiFile && uploads.length > 0">
        <div class="selected-files-panel">
          <div class="selected-files-header">Selected Files ({{ uploads.length }}):</div>
          <div
            v-for="upload in uploads"
            :key="upload.fileId"
            class="file-item"
            :class="{ 'file-item--has-errors': hasFileErrors(upload) }"
          >
            <div class="file-item-header">
              <div class="file-item-info">
                <span class="file-item-name">{{ upload.fileName }}</span>
                <span v-if="upload.fileParsed" class="file-item-rows">({{ upload.fileParsed.data.length }} rows)</span>
              </div>
              <div class="file-item-badges">
                <span v-if="upload.validationErrors.missing" class="badge badge-missing">Missing columns</span>
                <span v-if="upload.validationErrors.additional" class="badge badge-additional">Additional columns</span>
                <span v-if="upload.validationErrors.invalidCharacters" class="badge badge-invalid"
                  >Invalid characters</span
                >
                <span v-if="upload.validationErrors.validationReport" class="badge badge-validation"
                  >Validation errors</span
                >
                <span v-if="upload.badFileName" class="badge badge-invalid">Invalid filename</span>
                <span v-if="isFileValid(upload)" class="badge badge-valid">Valid</span>
              </div>
            </div>
            <div v-if="hasFileErrors(upload)" class="file-item-toggle">
              <a href="#" @click.prevent="toggleFileDetails(upload.fileId)">
                <font-awesome-icon
                  :icon="['fal', expandedFiles[upload.fileId] ? 'chevron-up' : 'chevron-down']"
                  class="mr-1"
                />
                {{ expandedFiles[upload.fileId] ? 'Hide details' : 'Show details' }}
              </a>
            </div>
            <div v-if="expandedFiles[upload.fileId] && hasFileErrors(upload)" class="file-item-errors">
              <div class="file-errors-content">
                <font-awesome-icon :icon="['fas', 'exclamation-circle']" class="error-icon" />
                <div class="error-details">
                  <div class="error-title">Column errors:</div>
                  <ul>
                    <li v-if="upload.validationErrors.missing">
                      <strong>Missing columns:</strong> {{ upload.validationErrors.missing }}
                    </li>
                    <li v-if="upload.validationErrors.additional">
                      <strong>Additional columns (not expected):</strong> {{ upload.validationErrors.additional }}
                    </li>
                    <li v-if="upload.validationErrors.invalidCharacters">
                      <strong>Invalid characters:</strong> {{ upload.validationErrors.invalidCharacters }}
                    </li>
                    <li v-if="upload.badFileName">
                      <strong>Invalid filename.</strong> Allowed:
                      {{ upload.validationErrors.allowedFileNames?.join(', ') }}
                    </li>
                  </ul>
                  <div v-if="upload.validationErrors.validationReport" class="mt-2">
                    <div class="error-title">Data errors:</div>
                    <ul>
                      <li v-for="item in upload.validationErrors.validationReport" :key="item">
                        {{ item }}
                      </li>
                    </ul>
                  </div>
                  <div class="error-help">Please fix the column headers or contact support</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-if="canUpload">
        <div class="panel-tag mt-3">
          You can now upload the {{ canUploadMultiFile && uploads.length > 1 ? 'files' : 'file' }}
          <ul v-if="!bypassValidation && entity && !canUploadMultiFile && !isPgpEnabled">
            <li>Correct headers found</li>
          </ul>
        </div>
        <div class="d-flex">
          <b-button @click="uploadCsv" :disabled="uploading || metadataErrors" variant="primary">
            Upload{{ canUploadMultiFile && uploads.length > 1 ? ` ${uploads.length} Files` : '' }}
          </b-button>

          <div class="ml-3">
            <app-loading :loading="uploading === true" />
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import Papa from 'papaparse';
import vueFilePond from 'vue-filepond';
import 'filepond/dist/filepond.min.css';

import ApiClient from '@/ApiClient.js';
import AppLoading from '@/components/AppLoading.vue';
import SmeAlert from '@/components/atoms/SmeAlert.vue';
import IntegrationsState from '@/state/IntegrationsState';
import State from '@/state/State';
import useFeatureFlags from '@/composables/useFeatureFlags';

const FilePond = vueFilePond(FilePondPluginFileValidateType);
const textDecoder = new TextDecoder();

function createEmptyUpload() {
  return {
    fileId: null,
    fileContent: undefined,
    fileParsed: undefined,
    fileName: undefined,
    validationErrors: {},
    badFileName: false,
  };
}

export default {
  name: 'SftpBulkUpload',
  components: { AppLoading, FilePond, SmeAlert },
  props: {
    entity: String,
    payScheduleId: String,
    title: String,
    metadata: Object,
    metadataErrors: Boolean,
    columns: Array,
    postFunction: Function,
    genericUpload: Boolean,
    emitError: Boolean,
  },
  data() {
    this.meta;
    return {
      error: undefined,
      uploads: [],
      expandedFiles: {},
      uploading: false,
      successfullyValidated: false,
      validationSkipped: false,
      skipReason: '',
      validating: false,
      state: State.state,
      integrationsState: IntegrationsState,
    };
  },
  setup() {
    const { canValidateSftpBulkUpload, canUploadMultiFile } = useFeatureFlags();
    return {
      canValidateSftpBulkUpload,
      canUploadMultiFile,
    };
  },
  methods: {
    onAddFile(error, fileItem) {
      this.error = undefined;
      this.successfullyValidated = false;
      this.validationSkipped = false;
      this.skipReason = '';
      this.validating = false;

      if (error) {
        return;
      }

      const file = fileItem.file;
      const fileId = fileItem.id;

      const existingIndex = this.uploads.findIndex(u => u.fileId === fileId);
      if (existingIndex !== -1) {
        return;
      }

      const upload = createEmptyUpload();
      upload.fileId = fileId;
      upload.fileName = file.name;
      this.uploads.push(upload);

      const fileReader = new FileReader();
      fileReader.onloadend = e => this.handleFileRead(e, fileId);
      fileReader.readAsArrayBuffer(file);
    },
    handleFileRead(e, fileId) {
      const upload = this.uploads.find(u => u.fileId === fileId);
      if (!upload) {
        return;
      }

      upload.fileContent = new Uint8Array(e.target.result);

      if (this.isPgpEnabled) {
        this.validationSkipped = true;
        this.skipReason = 'Validation preview is unavailable for PGP-encrypted uploads.';
        upload.validationErrors = {};
        upload.badFileName = false;
        return;
      }

      if (this.bypassValidation) {
        return;
      }

      if (this.requiredFileNames.length > 0 && !this.requiredFileNames.includes(upload.fileName)) {
        upload.badFileName = true;
        upload.validationErrors['allowedFileNames'] = this.requiredFileNames;
        return;
      }

      const fileAsString = textDecoder.decode(upload.fileContent).replace(/^\uFEFF/, '');

      const that = this;
      Papa.parse(fileAsString, {
        header: true,
        complete(results) {
          upload.fileParsed = results;
          that.updateValidationErrorsForUpload(upload);
        },
        error(parseErrors) {
          upload.fileParsed = parseErrors;
        },
      });
    },
    onRemoveFile(error, fileItem) {
      if (error) {
        return;
      }

      const fileId = fileItem.id;
      const index = this.uploads.findIndex(u => u.fileId === fileId);
      if (index !== -1) {
        this.uploads.splice(index, 1);
        delete this.expandedFiles[fileId];
      }

      if (this.uploads.length === 0) {
        this.successfullyValidated = false;
        this.validationSkipped = false;
        this.skipReason = '';
        this.validating = false;
        this.error = undefined;
        this.$emit('fileRemoved');
      }
    },
    updateValidationErrorsForUpload(upload) {
      if (!upload.fileContent || !upload.fileParsed) {
        upload.validationErrors = {};
        return;
      }

      let csvColumns = upload.fileParsed.meta.fields;

      function arrayDiff(arr1, arr2) {
        return arr1
          .filter(x => !arr2.includes(x))
          .map(x => '"' + x + '"')
          .join(', ');
      }

      if (this.state.company.properties?.portal?.remove_periods_in_uploaded_file_headers) {
        csvColumns = csvColumns.map(column => column.replace(/\./g, '').trim());
      } else {
        csvColumns = csvColumns.map(column => column.trim());
      }

      if (this.integrationsState.getIsAirflowPlatformEnabled()) {
        csvColumns = csvColumns.map(x => x.replaceAll(' ', '_'));
      }

      const errors = {
        missing: arrayDiff(this.requiredColumns, csvColumns),
        additional: arrayDiff(csvColumns, this.requiredColumns),
      };

      try {
        let decoded_content = textDecoder.decode(upload.fileContent);
        const lines = decoded_content.split('\n');
        if (lines.length > 0 && this.state.company.properties?.portal?.remove_periods_in_uploaded_file_headers) {
          lines[0] = lines[0].replace(/\./g, '');
        }
        decoded_content = lines.join('\n');
        if (this.state.company.properties?.portal?.skip_unicode_apostrophe_characters_in_uploads) {
          decoded_content = decoded_content.replaceAll('\u2019', "'");
        }
        decoded_content = decoded_content
          // eslint-disable-next-line no-control-regex
          .replace(/[^\x00-\x7F]/g, '');
        btoa(decoded_content);
      } catch {
        errors.invalidCharacters = 'The file has invalid characters, please fix them before uploading the file';
      }

      if (errors.missing || errors.additional || errors.invalidCharacters) {
        upload.validationErrors = errors;
      } else {
        upload.validationErrors = {};
      }
    },
    toggleFileDetails(fileId) {
      this.$set(this.expandedFiles, fileId, !this.expandedFiles[fileId]);
    },
    hasFileErrors(upload) {
      return (
        upload.validationErrors.missing ||
        upload.validationErrors.additional ||
        upload.validationErrors.invalidCharacters ||
        upload.validationErrors.validationReport ||
        upload.badFileName
      );
    },
    hasBlockingErrors(upload) {
      return (
        upload.validationErrors.missing ||
        upload.validationErrors.invalidCharacters ||
        upload.validationErrors.validationReport ||
        upload.badFileName
      );
    },
    isFileValid(upload) {
      return upload.fileContent && !this.hasBlockingErrors(upload);
    },
    async validateCsv() {
      this.uploading = true;
      this.validating = true;
      this.error = undefined;
      this.validationSkipped = false;
      this.skipReason = '';

      let allPassed = true;
      let skipped = false;

      for (const upload of this.uploads) {
        const fileName = this.keepOriginalFileName ? upload.fileName : this.entity + '.csv';
        const formattedFileContent = this.formatHeadersForValidation(upload.fileContent);

        try {
          const response = await ApiClient.validateCitransFile(
            this.state.company.company_id,
            fileName,
            formattedFileContent,
            this.entity.toLowerCase(),
          );

          // PGP-encrypted uploads can't be previewed; the backend skips validation and the
          // async ingestion pipeline validates after decryption, so let the user upload as-is.
          if (response.data.validation_skipped) {
            skipped = true;
            this.skipReason =
              response.data.skip_reason || 'Validation preview is unavailable for PGP-encrypted uploads.';
            upload.validationErrors = {};
            upload.badFileName = false;
          } else if (!response.data.ok) {
            allPassed = false;
            this.handleValidationErrorsForUpload(upload, response.data);
          }
        } catch {
          allPassed = false;
          upload.validationErrors.validationReport = [
            'Validation request failed. Please try again or contact support.',
          ];
        }
      }

      this.uploading = false;
      this.validating = false;

      if (skipped) {
        this.validationSkipped = true;
        return;
      }

      if (allPassed) {
        this.successfullyValidated = true;
        const fileText = this.uploads.length > 1 ? 'All files' : 'File';
        this.$appToast(`${fileText} validation passed! No errors detected.`, {
          title: 'File Validation Success',
          variant: 'success',
        });
      } else {
        this.$appToast('File validation found issues', {
          title: 'File Validation',
          variant: 'warning',
        });
      }
    },
    formatHeadersForValidation(fileContent) {
      const fileAsString = textDecoder.decode(fileContent).replace(/^\uFEFF/, '');
      const lines = fileAsString.split('\n');

      if (lines.length === 0) {
        return fileContent;
      }

      let headerLine = lines[0];
      if (this.state.company.properties?.portal?.remove_periods_in_uploaded_file_headers) {
        headerLine = headerLine.replace(/\./g, '');
      }

      const headerParts = headerLine.split(',');
      const formattedHeaders = headerParts.map(header => {
        return header.replace(/^"/, '').replace(/"$/, '').trim();
      });

      lines[0] = formattedHeaders.join(',');

      return new TextEncoder().encode(lines.join('\n'));
    },
    handleValidationErrorsForUpload(upload, data) {
      if (data.readable_validation_report && Array.isArray(data.readable_validation_report)) {
        upload.validationErrors.validationReport = data.readable_validation_report.map(errorText =>
          this.processValidationError(errorText),
        );
      } else {
        upload.validationErrors.validationReport = ['Validation failed. Please check your file or contact support.'];
      }
      this.$set(this.expandedFiles, upload.fileId, true);
    },
    processValidationError(errorText) {
      const errorLower = errorText.toLowerCase();

      if (errorLower.includes('keyerror')) {
        const match = errorText.match(/KeyError.*?['"]([^'"]+)['"]/i);
        if (match) {
          return `Missing Required Field: "${match[1]}"`;
        }
        return 'Missing Required Data: Your file is missing a required field or column.';
      } else if (errorLower.includes('typeerror')) {
        return 'Data Format Issue: Some data in your file is in the wrong format.';
      } else if (errorLower.includes('transformerror')) {
        const match = errorText.match(/TransformError: Error executing code[^:]*: (.+?)(?:: type:|$)/i);
        if (match) {
          return `Data Validation Error: ${match[1]}`;
        }
      } else if (errorLower.includes('validation') || errorLower.includes('invalid')) {
        return `Data Validation Error: ${errorText}`;
      }
      return 'File Validation Failed: Please try again or contact support.';
    },
    async uploadCsv() {
      this.uploading = true;

      const postFunc = this.postFunction || ApiClient.postFile;
      let successCount = 0;
      const failedFiles = [];

      for (const upload of this.uploads) {
        const fileName = this.keepOriginalFileName || this.genericUpload ? upload.fileName : this.entity + '.csv';
        try {
          await postFunc(this.state.company.company_id, fileName, upload.fileContent, this.metadata);
          successCount++;
        } catch (err) {
          failedFiles.push({ fileName: upload.fileName, error: err });
        }
      }

      if (failedFiles.length === 0) {
        this.uploads = [];
        this.expandedFiles = {};
        this.$refs.file.removeFiles();
        this.successfullyValidated = false;
        const entity = this.entity ? this.entity?.charAt(0).toUpperCase() + this.entity?.slice(1) : '';
        const fileText = successCount > 1 ? `${successCount} ${entity} files have` : `${entity} file has`;
        this.$appToast(`${fileText} been uploaded`, {
          title: 'Successfully uploaded',
          variant: 'success',
        });
        this.error = undefined;
      } else {
        const failedNames = failedFiles.map(f => f.fileName).join(', ');
        if (this.emitError) {
          this.$emit('error', failedFiles[0].error);
        } else {
          this.error = `Failed to upload: ${failedNames}`;
        }
      }

      this.uploading = false;
    },
  },
  computed: {
    isPgpEnabled() {
      return this.integrationsState.getIsPgpEnabled();
    },
    singleUpload() {
      return this.uploads[0] || { validationErrors: {} };
    },
    hasBadFileName() {
      return this.uploads.some(u => u.badFileName);
    },
    hasFiles() {
      return this.uploads.length > 0 && this.uploads.some(u => u.fileContent);
    },
    allFilesValid() {
      if (this.uploads.length === 0) {
        return false;
      }
      return this.uploads.every(u => this.isFileValid(u));
    },
    canUpload() {
      if (!this.hasFiles) {
        return false;
      }
      if (this.$slots.extraErrors) {
        return false;
      }
      return this.allFilesValid;
    },
    validateButtonText() {
      if (this.validating) {
        return 'Validating...';
      }
      if (this.canUploadMultiFile && this.uploads.length > 1) {
        return `Validate ${this.uploads.length} Files (Optional)`;
      }
      return 'Validate File (Optional)';
    },
    bypassValidation() {
      return this.state.company.properties.portal !== undefined
        ? this.state.company.properties.portal.bypass_file_upload_validation
        : false;
    },
    enableFileValidation() {
      if (this.state.company.properties.portal !== undefined) {
        const enableEntityFileValidation = this.state.company.properties.portal?.enable_entity_file_validation || [];
        return enableEntityFileValidation.includes(this.entity?.toUpperCase());
      } else {
        return false;
      }
    },
    showValidationForEntity() {
      const validationEntities = ['employees', 'expanded_employees', 'shifts', 'absences', 'jobs'];
      return validationEntities.includes(this.entity?.toLowerCase());
    },
    displayColumns() {
      return this.requiredColumns.map(c => '"' + c + '"').join(', ');
    },
    emptyFileLabel() {
      const fileText = this.canUploadMultiFile ? 'files' : 'a file';
      return `<i class="fal fa-cloud-upload"></i> Drop ${fileText} here or <u>browse</u> your files...`;
    },
    requiredColumns() {
      if (this.entity === undefined) {
        return null;
      }
      if (this.columns) {
        return this.columns;
      }
      const requiredColumns = this.integrationsState.getRequiredColumns(this.entity);
      if (requiredColumns === undefined) {
        this.$emit('error', `No columns defined for ${this.entity}`);
      }
      return requiredColumns;
    },
    acceptedFileTypes() {
      if (this.genericUpload) {
        return [
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'text/plain',
          'application/pdf',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'text/csv',
        ];
      }
      return this.bypassValidation ? undefined : ['text/csv', 'text/plain', 'application/vnd.ms-excel'];
    },
    keepOriginalFileName() {
      if (this.state.company.properties.portal !== undefined) {
        const keepOriginalFileNameOnUpload =
          this.state.company.properties.portal.keep_original_file_name_on_upload || [];
        return keepOriginalFileNameOnUpload.includes(this.entity?.toUpperCase());
      } else {
        return false;
      }
    },
    requiredFileNames() {
      if (this.state.company.properties.portal !== undefined) {
        const requireSpecificFileNamesOnUpload =
          this.state.company.properties.portal.require_specific_file_names_on_upload || [];
        return requireSpecificFileNamesOnUpload
          .filter(a => a.entity === this.entity?.toUpperCase())
          .flatMap(element => element.files_list);
      } else {
        return [];
      }
    },
  },
};
</script>

<style lang="scss">
.filepond--drop-label {
  border: 2px dashed var(--palette-color-brand-primary);
  border-radius: 0.5em;
  box-sizing: border-box;
  cursor: pointer;
  label {
    cursor: pointer;
  }
}

.filepond--wrapper {
  margin-bottom: 2rem !important;
  margin-top: 2rem !important;
}

.selected-files-panel {
  border-left: 4px solid var(--palette-color-brand-primary);
  background: var(--palette-color-default-lighten-95);
  padding: 1rem;
  margin-top: 1rem;
}

.selected-files-header {
  font-weight: 600;
  margin-bottom: 1rem;
}

.file-item {
  background: var(--palette-color-base-white);
  border-left: 3px solid var(--palette-color-default-lighten-80);
  padding: 0.75rem 1rem;
  margin-bottom: 0.5rem;

  &--has-errors {
    border-left-color: var(--palette-color-danger);
  }
}

.file-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.file-item-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.file-item-name {
  font-weight: 500;
}

.file-item-rows {
  color: var(--palette-color-default-lighten-30);
  font-size: 0.9em;
}

.file-item-badges {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.badge {
  padding: 0.35em 0.65em;
  font-size: 0.75em;
  font-weight: 500;
  border-radius: 0.25rem;
}

.badge-missing {
  background-color: var(--palette-color-danger);
  color: var(--palette-color-base-white);
}

.badge-additional {
  background-color: var(--palette-color-warning);
  color: var(--palette-color-default);
}

.badge-invalid {
  background-color: var(--palette-color-danger);
  color: var(--palette-color-base-white);
}

.badge-validation {
  background-color: var(--palette-color-danger);
  color: var(--palette-color-base-white);
}

.badge-valid {
  background-color: var(--palette-color-success);
  color: var(--palette-color-base-white);
}

.file-item-toggle {
  margin-top: 0.5rem;

  a {
    color: var(--palette-color-brand-primary);
    text-decoration: none;
    font-size: 0.9em;

    &:hover {
      text-decoration: underline;
    }
  }
}

.file-item-errors {
  margin-top: 0.75rem;
}

.file-errors-content {
  display: flex;
  gap: 0.75rem;
  background: var(--palette-color-danger-lighten-90);
  border: 1px solid var(--palette-color-danger-lighten-80);
  border-radius: 0.25rem;
  padding: 1rem;
}

.error-icon {
  color: var(--palette-color-danger);
  font-size: 1.25rem;
  flex-shrink: 0;
}

.error-details {
  flex: 1;

  ul {
    margin: 0.5rem 0;
    padding-left: 1.25rem;
  }

  li {
    margin-bottom: 0.25rem;
  }
}

.error-title {
  color: var(--palette-color-danger);
  font-weight: 600;
}

.error-help {
  color: var(--palette-color-default-lighten-30);
  font-size: 0.9em;
  margin-top: 0.5rem;
}
</style>
